import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Stack, SxProps } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { AppTypography } from '../app-typography';
import { StackColumn, StackRowForm } from '../stack-column';
import Image from '../Image';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { CubesIcon } from 'src/theme/overrides/CustomIcons';
import useResponsive from 'src/hooks/useResponsive';
import { FeatureBadgeV2 } from '../subscription/AccountBadge';
import HubspotContactForm from '../forms/HubspotContactForm';
import { contactFormConfig } from '../forms/hubspot-config';
import { useToggle } from 'src/hooks/useToggle';
import useSubscriptions from 'src/hooks/useSubscriptions';
import { getFeaturesRequirementsAPI } from 'src/resources/api/features/index.api';
import { AppButton } from '../app-button';
import { TopNavigationItems } from 'src/layouts/dashboard/navbar/NavConfig';

//
//

type NoFeatureAccessBannerProps = {
  title: string;
  description: string | string[];
  isNavigation?: boolean;
  planTier?: number;
  featureCode?: string;
  menuSection?: string;
  sx?: SxProps;
};

export const NoFeatureAccessBanner: React.FC<NoFeatureAccessBannerProps> = ({
  title,
  description,
  isNavigation,
  planTier,
  featureCode,
  menuSection,
  sx
}) => {
  const toggle = useToggle();
  const {
    startTrial,
    activateYourPlansMessage,
    showStartTrialMessage,
    subscriptionsAreLoading,
    userOnFreePlan,
    isPremiumUser,
  } = useSubscriptions();

  const smUp = useResponsive('up', 'sm');
  const mdDown = useResponsive('down', 'md');

  const getMerchantFeaturesRequirementsHook = useQuery({
    queryKey: ['features-requirements'],
    queryFn: () => getFeaturesRequirementsAPI(),
  });

  const featurePlan = useMemo(() => {
    return getMerchantFeaturesRequirementsHook?.data?.find(
      (plan: { featureCode: string }) => plan?.featureCode === featureCode
    )?.requiredPlan;
  }, [featureCode, getMerchantFeaturesRequirementsHook?.data]);

  const ChooseOrUpgradePlanCTA = ({ title }: { title?: string }) => {
    return (
      <AppButton
        component={RouterLink}
        to={PATH_DASHBOARD.user.plans}
        variant="contained"
        color="secondary"
        className="brandcube"
        endIcon={<CubesIcon size="small" />}
      >
        {title || 'Choose a plan'}
      </AppButton>
    );
  };

  const StartTrialCTA = () => {
    return (
      <AppButton loading={subscriptionsAreLoading} onClick={startTrial} variant="text" color="carbon">
        Start trial
      </AppButton>
    );
  };

  const RequestTrialCTA = () => {
    return (
      <AppButton onClick={toggle.on} variant="text" color="carbon">
        Request trial
      </AppButton>
    );
  };

  const ContactUsCTA = () => {
    return (
      <AppButton variant="text" color="carbon" onClick={toggle.on}>
        Contact our team
      </AppButton>
    );
  };

  //

  return (
    <StackRowForm
      p={4}
      gap={{ xs: 6, sm: 4 }}
      alignItems="center"
      sx={{
        backgroundColor: (t) => isNavigation ? t.palette.primary.lighter : 'white',
        flex: 1,
        alignItems: isNavigation ? 'flex-start' : 'center',
        ...sx
      }}
      // sx={{ backgroundColor: (t) => isNavigation ? t.palette.primary.lighter : 'white', flex: 1, alignItems: isNavigation ? 'flex-start' : 'center' }}
    >
      <StackColumn
        gap={isNavigation ? 3 : 4}
        sx={{ '& .MuiTypography-body1': { fontSize: isNavigation ? 14 : 'initial' } }}
      >
        <StackColumn gap={1}>
          {!isNavigation && <AppTypography variant="h4">{title}</AppTypography>}

          <FeatureBadgeV2
            label={`Available on ${
              featurePlan === 20 || planTier === 20 ? 'Starter'
                : featurePlan === 30 || planTier === 30 ? 'Advanced'
                : 'Enterprise'
            } plan`}
          />

          {isNavigation && (
            <AppTypography fontWeight="bold" mt={1}>
              {title}
            </AppTypography>
          )}
        </StackColumn>

        <StackColumn gap={1}>
          {description &&
            (!!Array.isArray(description) ? description : [description])?.map((text) => (
              <AppTypography key={text}>{text}</AppTypography>
            ))}
        </StackColumn>

        <StackRowForm
          alignItems={{ xs: 'center', sm: 'initial' }}
          sx={{ '& .MuiButtonBase-root': { fontWeight: '400 !important' }, '& .MuiButton-contained': { maxWidth: 220, width: '100%' } }}
        >
          {/* CTAs */}
          {(userOnFreePlan || menuSection === TopNavigationItems.offsets) && <ChooseOrUpgradePlanCTA />}
          {isPremiumUser && menuSection !== TopNavigationItems.offsets && <ChooseOrUpgradePlanCTA title="Upgrade plan" />}
          {showStartTrialMessage && menuSection !== TopNavigationItems.programmes && <StartTrialCTA />}
          {activateYourPlansMessage && menuSection !== TopNavigationItems.programmes && <RequestTrialCTA />}
          {menuSection === 'programmes' && <ContactUsCTA />}
        </StackRowForm>
      </StackColumn>

      {!(smUp && mdDown) && !isNavigation && (
        <Stack>
          <Image
            src="https://res.cloudinary.com/carbonclick/image/upload/v1688641820/Merchant%20Portal/form-icon_wn1spw.svg"
            sx={{ height: 'auto', mr: 3, width: 140 }}
            alt="custom solution banner image"
          />
        </Stack>
      )}

      {toggle.value && (
        <HubspotContactForm
          isModalOpen={toggle.value}
          setIsModalOpen={toggle.setValue}
          region={contactFormConfig.region}
          portalId={contactFormConfig.portalId}
          formId={contactFormConfig.formId}
        />
      )}
    </StackRowForm>
  );
};
